import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * @typedef {Object} CollectionTabsRefs
 * @property {HTMLElement | undefined} tablist
 * @property {HTMLElement | undefined} panel
 */

/**
 * Slideshows only enable scrolling (and therefore touch swiping) while they carry these
 * runtime attributes. The section HTML we fetch per tab never has them, so a plain morph
 * would strip them off the reused element and leave the mobile carousel frozen — the
 * viewport observer won't re-add them because the slideshow never left the viewport.
 */
const SLIDESHOW_RUNTIME_ATTRIBUTES = ['in-viewport', 'actioned'];

/** @type {import('@theme/morph').Options} */
const TAB_MORPH_OPTIONS = {
  ...MORPH_OPTIONS,
  childrenOnly: true,
  onBeforeUpdate(oldNode, newNode) {
    MORPH_OPTIONS.onBeforeUpdate?.(oldNode, newNode);

    if (!(oldNode instanceof Element) || !(newNode instanceof Element)) return;
    if (oldNode.tagName !== 'SLIDESHOW-COMPONENT') return;

    for (const attribute of SLIDESHOW_RUNTIME_ATTRIBUTES) {
      if (oldNode.hasAttribute(attribute)) {
        newNode.setAttribute(attribute, oldNode.getAttribute(attribute) ?? '');
      }
    }
  },
};

/**
 * Tabbed collection products — loads products via the Section Rendering API.
 * @extends {Component<CollectionTabsRefs>}
 */
class CollectionTabsComponent extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleTabClick, true);
    this.addEventListener('keydown', this.#handleKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleTabClick, true);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.#activeFetch?.abort();
  }

  /**
   * @param {MouseEvent} event
   */
  #handleTabClick = (event) => {
    const tab = this.#getTabFromEvent(event);
    if (!tab) return;

    event.preventDefault();
    event.stopPropagation();
    this.#activateTab(tab);
  };

  /**
   * @param {Event} event
   * @returns {HTMLButtonElement | null}
   */
  #getTabFromEvent(event) {
    const path = event.composedPath?.() ?? [];

    for (const node of path) {
      if (node instanceof HTMLButtonElement && node.hasAttribute('data-collection-tab') && this.contains(node)) {
        return node;
      }
    }

    let element = event.target;
    if (!(element instanceof Element)) {
      element = element instanceof Node ? element.parentElement : null;
    }

    const tab = element instanceof Element ? element.closest('[data-collection-tab]') : null;
    return tab instanceof HTMLButtonElement && this.contains(tab) && !tab.disabled ? tab : null;
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    const tab = this.#getTabFromEvent(event);
    if (!tab) return;

    const tabs = Array.from(this.querySelectorAll('[data-collection-tab]')).filter(
      (element) => element instanceof HTMLButtonElement && !element.disabled
    );
    const currentIndex = tabs.indexOf(tab);
    if (currentIndex === -1) return;

    let nextIndex = -1;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = tabs[nextIndex];
    if (!(nextTab instanceof HTMLButtonElement)) return;

    nextTab.focus();
    this.#activateTab(nextTab);
  };

  /**
   * @param {HTMLButtonElement} tab
   */
  #activateTab(tab) {
    if (tab.disabled || tab.getAttribute('aria-selected') === 'true') return;

    const handle = tab.dataset.collectionHandle;
    if (!handle) return;

    this.#selectTab(tab);
    this.#loadCollection(handle);
  }

  /**
   * @param {HTMLButtonElement} tab
   */
  #selectTab(tab) {
    const tabs = this.querySelectorAll('[data-collection-tab]');

    for (const element of tabs) {
      if (!(element instanceof HTMLButtonElement)) continue;
      const isSelected = element === tab;
      element.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      element.setAttribute('tabindex', isSelected ? '0' : '-1');
    }
  }

  /**
   * @param {string} handle
   */
  async #loadCollection(handle) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const panel = this.querySelector('[data-collection-tabs-products]');
    if (!(panel instanceof HTMLElement)) return;

    panel.setAttribute('aria-busy', 'true');

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    const { signal } = this.#activeFetch;

    const localePrefix = (window.location.pathname || '').match(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i)?.[0] || '';
    const url = new URL(`${localePrefix}/collections/${handle}`, window.location.origin);
    const useCache = typeof Shopify === 'undefined' || !Shopify.designMode;

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, useCache, url);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newPanel = doc.querySelector('[data-collection-tabs-products]');

      if (newPanel instanceof HTMLElement) {
        morph(panel, newPanel, TAB_MORPH_OPTIONS);
        this.#refreshProductCards(panel);
        this.#refreshCarousels(panel);
        window.__ASTERI_MF_REAPPLY__?.();
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('Collection tabs error:', error);
    } finally {
      if (!signal.aborted) {
        panel.removeAttribute('aria-busy');
      }
      this.#activeFetch = null;
    }
  }

  /**
   * Re-initializes shade galleries after the product grid is morphed.
   * @param {HTMLElement} panel
   */
  #refreshProductCards(panel) {
    requestAnimationFrame(() => {
      for (const card of panel.querySelectorAll('product-card')) {
        card.dispatchEvent(new CustomEvent('theme:product-card-refresh', { bubbles: false }));
      }
    });
  }

  /**
   * The morph swaps slides in and out of a slideshow that stays mounted, so its slide
   * visibility tracking still points at the previous tab's slides. Rebuild it and rewind
   * the scroller so the new tab starts on its first product.
   * @param {HTMLElement} panel
   */
  #refreshCarousels(panel) {
    requestAnimationFrame(() => {
      for (const element of panel.querySelectorAll('slideshow-component')) {
        const scroller = element.querySelector('slideshow-slides');
        if (scroller instanceof HTMLElement) {
          scroller.scrollTo({ left: 0, top: 0, behavior: 'instant' });
        }

        const slideshow = /** @type {any} */ (element);
        if (typeof slideshow.ensureControls === 'function') slideshow.ensureControls();
      }
    });
  }
}

if (!customElements.get('collection-tabs-component')) {
  customElements.define('collection-tabs-component', CollectionTabsComponent);
}
